<script lang="ts">
	import { browser } from '$app/environment';
	import { enhance } from '$app/forms';
	import { goto } from '$app/navigation';
	import SocialLogin from '$lib/components/SocialLogin.svelte';
	import InputControl from '$lib/components/forms/InputControl.svelte';
	import { auth, getCodeMessage, getFirebaseCode } from '$lib/firebase';
	import { route } from '$lib/routes';
	import type { Mode } from '$lib/types';
	import type { SubmitFunction } from '@sveltejs/kit';
	import { getAnalytics, logEvent } from 'firebase/analytics';
	import {
		EmailAuthProvider,
		createUserWithEmailAndPassword,
		linkWithCredential,
		signInWithEmailAndPassword,
	} from 'firebase/auth';
	import { fade } from 'svelte/transition';

	export let state: Mode = 'login';
	export let next: string | undefined = undefined;

	export let email = '';
	export let emailError = '';

	let password: string;
	let loading = false;
	$: disabled = loading || !email || !password;

	let socialError: string;
	let forgotSuccess: boolean;

	const passwordLogin = async () => {
		console.debug('attempting to log in with email and password', email);
		const cred = await signInWithEmailAndPassword(auth, email, password);
		console.debug('successfully logged in with email and password', cred.user.uid);
		logEvent(getAnalytics(), 'login', {
			method: 'email',
			path: window.location.pathname,
		});
		return await cred.user.getIdToken();
	};

	const passwordSignup = async () => {
		logEvent(getAnalytics(), 'sign_up', {
			method: 'email',
			path: window.location.pathname,
		});
		if (auth.currentUser?.isAnonymous) {
			if (!browser) throw new Error('passwordsignup is only available in the browser.');
			console.debug('converting anonymous user to email and password', email);
			const credential = EmailAuthProvider.credential(email, password);
			const cred = await linkWithCredential(auth.currentUser, credential);
			console.debug(
				'successfully converted anonymous user to email and password',
				auth.currentUser.uid,
			);
			return await cred.user.getIdToken();
		} else {
			console.debug('attempting to create user with email and password', email);
			const cred = await createUserWithEmailAndPassword(auth, email, password);
			console.debug('successfully created user with email and password', cred.user.uid);
			return await cred.user.getIdToken();
		}
	};

	const loginHandler = (tokenFunc: () => Promise<string>): SubmitFunction => {
		return async ({ formData, cancel }) => {
			try {
				loading = true;
				const token = await tokenFunc();
				console.debug('attempting to get session cookie');
				formData.forEach((_, key) => formData.delete(key));
				formData.set('token', token);
			} catch (error) {
				const code = getFirebaseCode(error);
				emailError = getCodeMessage(code);
				console.debug('cancelling form submission due to error', error);
				loading = false;
				cancel();
			}

			return async ({ result, update }) => {
				await update();
				loading = false;

				let code;
				switch (result.type) {
					case 'success':
						console.debug('successfully created session cookie');
						break;
					case 'failure':
						console.error('create session cookie failed', result.data);
						code = result.data?.email?.error;
						break;
					case 'error':
						console.error('create session cookie had error', result.error);
						code = result.error?.code;
						break;
				}
				if (code) emailError = getCodeMessage(code);
			};
		};
	};

	const handlePasswordLogin = loginHandler(passwordLogin);
	const handlePasswordSignup = loginHandler(passwordSignup);
	const handleForgotPassword: SubmitFunction = async () => {
		loading = true;
		console.debug('requesting forgot password email', email);

		return async ({ update, result }) => {
			await update();
			loading = false;

			let code;
			switch (result.type) {
				case 'success':
					forgotSuccess = true;
					break;
				case 'failure':
					console.error('send forgot password failed', result.data);
					code = result.data?.email?.error;
					break;
				case 'error':
					console.error('send forgot password had error', result.error);
					code = result.error?.code;
					break;
			}
			emailError = getCodeMessage(code);
		};
	};
</script>

<div class="container mx-auto my-4 max-w-lg px-4">
	{#if state === 'forgot'}
		<div in:fade class="mb-8 flex w-full flex-col gap-4">
			<h1 class="text-2xl font-bold">Forgot your password?</h1>
			{#if !forgotSuccess}
				<p>
					No problem, enter the email you registered with and we'll send a link to reset the
					password.
				</p>
				<form
					class="flex flex-col"
					method="POST"
					action="/login?/forgot"
					use:enhance={handleForgotPassword}>
					<InputControl label="Email" error={emailError}>
						<input
							bind:value={email}
							required
							autocomplete="email"
							disabled={loading}
							name="email"
							type="email"
							class="input input-bordered"
							placeholder="Email" />
					</InputControl>

					<button type="submit" class="btn btn-primary mt-8" disabled={loading || !email}>
						{#if loading}
							<div class="loading loading-spinner h-4 w-4" />
						{:else}
							Send link
						{/if}
					</button>
				</form>
			{:else}
				<p>
					We've sent a link to reset your password to <strong>{email}</strong>. Please check your
					email.
				</p>
			{/if}
		</div>
		<a href="/login" on:click|preventDefault={() => (state = 'login')} class="link">
			Back to login
		</a>
	{/if}
	{#if ['login', 'signup'].includes(state)}
		<div role="tablist" class="tabs tabs-bordered w-full" in:fade>
			<input
				name="login"
				type="radio"
				aria-label="Log in"
				role="tab"
				class="tab whitespace-nowrap"
				value="login"
				bind:group={state}
				checked={state === 'login'} />
			{#key state}
				<div role="tabpanel" class="tab-content" in:fade>
					<h1 class="my-8 text-2xl font-bold">Log in to your account</h1>
					{#if socialError}
						<p class="text-red-500">{socialError}</p>
					{/if}
					<div class="mb-8 flex flex-col gap-4">
						<SocialLogin
							on:error={({ detail }) => (socialError = detail.message)}
							on:user={() => goto(next ?? route.root())} />
						<p>Or log in with an email</p>
						<form
							method="POST"
							action="/login?/login&next={next ?? route.root()}"
							class="flex flex-col"
							use:enhance={handlePasswordLogin}>
							<InputControl label="Email" error={emailError}>
								<input
									bind:value={email}
									required
									autocomplete="email"
									disabled={loading}
									name="email"
									type="email"
									class="input input-bordered"
									placeholder="Email" />
							</InputControl>
							<InputControl label="Password">
								<input
									bind:value={password}
									required
									minlength="6"
									autocomplete="current-password"
									disabled={loading}
									name="password"
									type="password"
									class="input input-bordered"
									placeholder="Password" />
							</InputControl>

							<button type="submit" class="no-animate btn btn-primary mt-8" {disabled}>
								{#if loading}
									<div class="loading loading-spinner h-4 w-4" />
								{:else}
									Log in
								{/if}
							</button>
						</form>
					</div>
					<a
						href="/forgot-password"
						on:click|preventDefault={() => (state = 'forgot')}
						class="link font-light text-neutral-500">
						Forgot password?
					</a>
				</div>
			{/key}
			<input
				name="login"
				type="radio"
				aria-label="Register"
				role="tab"
				class="tab whitespace-nowrap font-bold"
				value="signup"
				bind:group={state}
				checked={state === 'signup'} />

			{#key state}
				<div role="tabpanel" class="tab-content" in:fade>
					<h1 class="my-8 text-2xl font-bold">Registration is now closed</h1>
					<p>
						We're sorry, but we're not accepting new registrations at this time. If you have an
						account, you can still log in.
					</p>
				</div>
			{/key}
		</div>
	{/if}
</div>
